import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Eye as EyeIcon,
  Mail as MailIcon,
  Moon as MoonIcon,
  ShieldCheck as ShieldCheckIcon,
  Sun as SunIcon,
  Wallet as WalletIcon,
} from 'lucide-react';
import { GoogleIcon } from '../../components/common/BrandIcons';
import { ConnectWallet } from '@/components/auth/ConnectWallet';
import { EmailLogin } from '@/components/auth/EmailLogin';
import { GoogleLogin } from '@/components/auth/GoogleLogin';
import { TermsModal, PrivacyModal } from '@/components/legal/LegalModals';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { toggleTheme } from '@/store/themeSlice';
import { exitDemoMode } from '@/store/authSlice';
import { exitDemo } from '@/demo/seedDemoStore';
import '@/styles/global.css';

type LoginMethod = 'wallet' | 'email' | 'google';

// Check if Google login is configured
const GOOGLE_ENABLED = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);

export function LoginPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isDemo = useAppSelector((state) => state.auth.isDemo);
  const isDark = useAppSelector((state) => state.theme.isDark);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-detect Google OAuth callback (code or error in URL)
  const isGoogleCallback = Boolean(searchParams.get('code') || searchParams.get('error'));
  // WK-first: SSP auth hierarchy makes the wallet the primary method (a first
  // login has no alternative), so the SSP tab is the default. Google is only
  // auto-selected when returning from an OAuth redirect.
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(
    isGoogleCallback && GOOGLE_ENABLED ? 'google' : 'wallet'
  );
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    if (isAuthenticated) {
      void navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // The login page is the real-auth surface. A tab that ever explored the demo
  // keeps a tab-scoped `ssp_demo` flag that maybeRehydrateDemo() re-enters as
  // demo mode regardless of route — which leaves the demo network firewall
  // active and hard-rejects the real Google/email login with DemoModeError
  // ("This action is unavailable in the live demo"). Tearing the demo down on
  // mount (which also clears the sessionStorage flag, so re-entry can't fire
  // again) guarantees a clean real-auth surface however the user arrived here.
  useEffect(() => {
    if (isDemo) {
      exitDemo();
      dispatch(exitDemoMode());
    }
  }, [isDemo, dispatch]);

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  return (
    <div className="login-page">
      <button
        className="theme-toggle"
        onClick={handleThemeToggle}
        title={t('login.toggleTheme')}
        aria-label={t('login.toggleTheme')}
      >
        {isDark ? <SunIcon /> : <MoonIcon />}
      </button>

      <div className="login-content">
        <img
          src={isDark ? '/ssp-logo-white.svg' : '/ssp-logo-black.svg'}
          alt="SSP Enterprise"
          className="login-logo"
        />
        <p className="login-tagline">{t('login.tagline')}</p>
        <h1 className="login-title font-display">{t('login.title')}</h1>
        <p className="login-subtitle">{t('login.subtitle')}</p>

        <div className="login-card">
          <div className="login-method-tabs">
            <button
              className={`login-method-tab ${loginMethod === 'wallet' ? 'active' : ''}`}
              onClick={() => {
                setLoginMethod('wallet');
              }}
            >
              <WalletIcon />
              <span>{t('login.tabSsp')}</span>
            </button>
            {GOOGLE_ENABLED && (
              <button
                className={`login-method-tab ${loginMethod === 'google' ? 'active' : ''}`}
                onClick={() => {
                  setLoginMethod('google');
                }}
              >
                <GoogleIcon />
                <span>{t('login.tabGoogle')}</span>
              </button>
            )}
            <button
              className={`login-method-tab ${loginMethod === 'email' ? 'active' : ''}`}
              onClick={() => {
                setLoginMethod('email');
              }}
            >
              <MailIcon />
              <span>{t('login.tabEmail')}</span>
            </button>
          </div>

          {loginMethod === 'wallet' && <ConnectWallet />}
          {loginMethod === 'email' && <EmailLogin />}
          {loginMethod === 'google' && <GoogleLogin />}
        </div>

        <div className="login-features">
          <span>{t('login.featureMultiAsset')}</span>
          <span className="feature-dot">•</span>
          <span>{t('login.featureMultiSignature')}</span>
          <span className="feature-dot">•</span>
          <span>{t('login.featureSelfCustody')}</span>
        </div>

        {/* Tertiary CTA, set apart from the auth card so it stays subtle: a
            no-install, read-only demo treasury. */}
        <button
          type="button"
          className="login-demo-link"
          data-testid="login-explore-demo"
          onClick={() => {
            void navigate('/demo');
          }}
        >
          <EyeIcon />
          <span>{t('demo.exploreDemo')}</span>
        </button>

        <div className="login-footer">
          <p className="login-footer-text">
            {t('login.newToSsp')}{' '}
            <a
              href="https://sspwallet.com"
              target="_blank"
              rel="noopener noreferrer"
              className="login-footer-link"
            >
              {t('login.getSspWallet')}
            </a>
          </p>
          <p className="login-footer-legal">
            <button
              className="login-footer-link-btn"
              onClick={() => {
                setTermsOpen(true);
              }}
            >
              {t('login.termsOfService')}
            </button>
            <span className="feature-dot">·</span>
            <button
              className="login-footer-link-btn"
              onClick={() => {
                setPrivacyOpen(true);
              }}
            >
              {t('login.privacyPolicy')}
            </button>
            <span className="feature-dot">·</span>
            {/* Trust signal: SSP's Halborn security audits (same canonical URL
                as sspwallet.io's security section / footer). */}
            <a
              href="https://www.halborn.com/audits/influx-technologies"
              target="_blank"
              rel="noopener noreferrer"
              className="login-footer-link-btn"
              data-testid="login-audited-by-halborn"
            >
              <ShieldCheckIcon /> {t('login.auditedByHalborn')}
            </a>
          </p>
        </div>
      </div>

      <TermsModal
        open={termsOpen}
        onClose={() => {
          setTermsOpen(false);
        }}
      />
      <PrivacyModal
        open={privacyOpen}
        onClose={() => {
          setPrivacyOpen(false);
        }}
      />
    </div>
  );
}

export default LoginPage;
